import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import useShowDemoUi from '../../hooks/useShowDemoUi';

const CONFIRM_DEMO = 'CLEAR_DEMO_TEST_DATA';
const CONFIRM_SEED = 'CLEAR_SEED_CATALOG_DATA';
const CONFIRM_FULL = 'DELETE_ALL_APPLICATION_DATA';

function scrollToHash(hash) {
  if (!hash || hash === '#') return;
  const id = hash.replace(/^#/, '');
  const el = document.getElementById(`danger-${id}`);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export default function AdminDataClearance() {
  const { user, isAdmin } = useAuth();
  const showDemoDataInUi = useShowDemoUi();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [busy, setBusy] = useState(null);

  const [demoConfirm, setDemoConfirm] = useState('');
  const [demoAck, setDemoAck] = useState(false);

  const [seedConfirm, setSeedConfirm] = useState('');
  const [seedAckImpact, setSeedAckImpact] = useState(false);
  const [seedAckEnv, setSeedAckEnv] = useState(false);

  const [fullConfirm, setFullConfirm] = useState('');
  const [fullEmail, setFullEmail] = useState('');
  const [fullAck1, setFullAck1] = useState(false);
  const [fullAck2, setFullAck2] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/data-danger/overview');
      setOverview(r.data?.data || null);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load clearance overview');
      setOverview(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    scrollToHash(location.hash);
  }, [location.hash, loading, overview]);

  const caps = overview?.capabilities || {};
  const preview = overview?.preview || {};
  const isSystemAdmin = Boolean(user?.is_system_admin);

  const runDemo = async () => {
    setBusy('demo');
    try {
      const r = await api.post('/admin/data-danger/clear-demo-test', {
        confirm: demoConfirm.trim(),
        archive_first: true
      });
      toast.success(r.data?.message || 'Demo/test data cleared');
      setDemoConfirm('');
      setDemoAck(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    } finally {
      setBusy(null);
    }
  };

  const runSeed = async () => {
    setBusy('seed');
    try {
      const r = await api.post('/admin/data-danger/clear-seed-catalogs', {
        confirm: seedConfirm.trim()
      });
      toast.success(r.data?.message || 'Seed catalogs cleared');
      const warns = r.data?.data?.warnings;
      if (Array.isArray(warns) && warns.length) {
        toast.info(`${warns.length} item(s) skipped — see server response or re-open overview.`);
      }
      setSeedConfirm('');
      setSeedAckImpact(false);
      setSeedAckEnv(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    } finally {
      setBusy(null);
    }
  };

  const runFull = async () => {
    setBusy('full');
    try {
      const r = await api.post('/admin/data-danger/clear-all-application-data', {
        confirm: fullConfirm.trim(),
        confirm_email: fullEmail.trim()
      });
      toast.success(r.data?.message || 'Database truncated');
      setFullConfirm('');
      setFullEmail('');
      setFullAck1(false);
      setFullAck2(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    } finally {
      setBusy(null);
    }
  };

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Administrator access required.</Typography>
      </Box>
    );
  }

  if (loading && !overview) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 880, mx: 'auto', pb: 8 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        Data clearance
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Tiered destructive actions. Impact increases downward: demo accounts only → seeded catalogs → entire application schema
        (excluding migration history).
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        Environment gates: <strong>Clear Seed Data</strong> is off in production unless{' '}
        <code>DATA_DANGER_ALLOW_SEED_CLEAR=true</code>. <strong>Clear All Data</strong> requires{' '}
        <code>DATA_DANGER_ALLOW_FULL_RESET=true</code> and, in production,{' '}
        <code>DATA_DANGER_ALLOW_FULL_RESET_IN_PRODUCTION=true</code>.
      </Alert>

      {showDemoDataInUi ? (
        <Card variant="outlined" id="danger-demo" sx={{ mb: 2, borderColor: 'warning.light' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Clear Demo/Test Data
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {caps.demo_clear?.summary}
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Preview: {preview.demo_users_found ?? '—'} / {preview.demo_users_expected ?? '—'} demo mailbox(es) found
              (hosting-linked domains).
            </Typography>
            <Divider sx={{ my: 2 }} />
            <FormGroup sx={{ mb: 2 }}>
              <FormControlLabel
                control={<Checkbox checked={demoAck} onChange={(e) => setDemoAck(e.target.checked)} />}
                label="I understand this archives then permanently deletes demo/template mailboxes for hosting-linked domains (and legacy template addresses), as described above."
              />
            </FormGroup>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Type <strong>{CONFIRM_DEMO}</strong> to enable the button.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              <TextField
                size="small"
                fullWidth
                label="Confirmation phrase"
                value={demoConfirm}
                onChange={(e) => setDemoConfirm(e.target.value)}
                disabled={busy === 'demo'}
              />
              <Button
                variant="contained"
                color="warning"
                disabled={busy === 'demo' || !demoAck || demoConfirm.trim() !== CONFIRM_DEMO}
                onClick={runDemo}
                sx={{ minWidth: 160 }}
              >
                {busy === 'demo' ? <CircularProgress size={22} color="inherit" /> : 'Clear demo/test'}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }} id="danger-demo">
          The <strong>Clear Demo/Test Data</strong> tier is hidden because demo UI is turned off. Turn on{' '}
          <strong>Show demo / test data in the application UI</strong> under Administration → Settings →{' '}
          <strong>Demo / Test Data</strong> to show this section (and the sidebar link).
        </Alert>
      )}

      <Card variant="outlined" id="danger-seed" sx={{ mb: 2, border: (t) => `2px solid ${t.palette.error.main}` }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'error.main' }}>
            Clear Seed Data
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {caps.seed_catalog_clear?.summary}
          </Typography>
          {!caps.seed_catalog_clear?.allowed ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {caps.seed_catalog_clear?.blocked_reason || 'Disabled.'}
            </Alert>
          ) : (
            <>
              <Typography variant="caption" component="pre" sx={{ display: 'block', mt: 1, whiteSpace: 'pre-wrap', fontSize: 11 }}>
                {JSON.stringify(
                  {
                    agents_question_bank_rows: preview.agents_question_bank_rows,
                    training_categories_removable: preview.training_categories_removable,
                    immigration_workflow_seed_rows: preview.immigration_workflow_seed_rows,
                    job_lookup_seed_rows: preview.job_lookup_seed_rows,
                    document_types_seed_rows: preview.document_types_seed_rows,
                    okr_areas_removable: preview.okr_objective_areas_removable,
                    okr_system_categories_removable: preview.okr_system_categories_removable
                  },
                  null,
                  2
                )}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <FormGroup sx={{ mb: 2 }}>
                <FormControlLabel
                  control={<Checkbox checked={seedAckImpact} onChange={(e) => setSeedAckImpact(e.target.checked)} />}
                  label="I understand modules may break until I re-run SEED Data → Run all seeds."
                />
                <FormControlLabel
                  control={<Checkbox checked={seedAckEnv} onChange={(e) => setSeedAckEnv(e.target.checked)} />}
                  label="I understand training categories and OKR seed rows are only removed when nothing references them."
                />
              </FormGroup>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Requires system administrator. Type <strong>{CONFIRM_SEED}</strong> to enable.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                <TextField
                  size="small"
                  fullWidth
                  label="Confirmation phrase"
                  value={seedConfirm}
                  onChange={(e) => setSeedConfirm(e.target.value)}
                  disabled={busy === 'seed' || !isSystemAdmin}
                />
                <Button
                  variant="contained"
                  color="error"
                  disabled={
                    busy === 'seed' ||
                    !isSystemAdmin ||
                    !caps.seed_catalog_clear?.allowed ||
                    !seedAckImpact ||
                    !seedAckEnv ||
                    seedConfirm.trim() !== CONFIRM_SEED
                  }
                  onClick={runSeed}
                  sx={{ minWidth: 160 }}
                >
                  {busy === 'seed' ? <CircularProgress size={22} color="inherit" /> : 'Clear seed data'}
                </Button>
              </Stack>
            </>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined" id="danger-all" sx={{ mb: 2, border: (t) => `2px solid ${t.palette.error.main}` }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'error.main' }}>
            Clear All Data
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {caps.full_application_reset?.summary}
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            Preview: {preview.full_reset_public_table_count ?? '—'} public tables would be truncated (migration table excluded).
          </Typography>
          {!caps.full_application_reset?.allowed ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {caps.full_application_reset?.blocked_reason || 'Disabled.'}
            </Alert>
          ) : (
            <>
              <Divider sx={{ my: 2 }} />
              <FormGroup sx={{ mb: 2 }}>
                <FormControlLabel
                  control={<Checkbox checked={fullAck1} onChange={(e) => setFullAck1(e.target.checked)} />}
                  label="I understand every application table will be emptied (including users, RBAC, and backups metadata on DB)."
                />
                <FormControlLabel
                  control={<Checkbox checked={fullAck2} onChange={(e) => setFullAck2(e.target.checked)} />}
                  label="I will run database migrations if needed and SEED Data → Run all before using the system."
                />
              </FormGroup>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                System administrator only. Type <strong>{CONFIRM_FULL}</strong> and your login email{' '}
                <strong>{user?.email || '(unknown)'}</strong>.
              </Typography>
              <Stack spacing={1.5}>
                <TextField
                  size="small"
                  fullWidth
                  label="Confirmation phrase"
                  value={fullConfirm}
                  onChange={(e) => setFullConfirm(e.target.value)}
                  disabled={busy === 'full' || !isSystemAdmin}
                />
                <TextField
                  size="small"
                  fullWidth
                  label="Type your login email to confirm"
                  value={fullEmail}
                  onChange={(e) => setFullEmail(e.target.value)}
                  disabled={busy === 'full' || !isSystemAdmin}
                  autoComplete="off"
                />
                <Button
                  variant="contained"
                  color="error"
                  disabled={
                    busy === 'full' ||
                    !isSystemAdmin ||
                    !caps.full_application_reset?.allowed ||
                    !fullAck1 ||
                    !fullAck2 ||
                    fullConfirm.trim() !== CONFIRM_FULL ||
                    fullEmail.trim().toLowerCase() !== String(user?.email || '').trim().toLowerCase()
                  }
                  onClick={runFull}
                  sx={{ alignSelf: 'flex-start', minWidth: 200 }}
                >
                  {busy === 'full' ? <CircularProgress size={22} color="inherit" /> : 'Clear all application data'}
                </Button>
              </Stack>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
